import {
  ArrowUturnLeftIcon,
  BookmarkIcon,
  CheckIcon,
  DocumentTextIcon,
  EyeIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import React, { useMemo } from "react";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import { avatarInitial } from "../utils/mailContactAvatar";

const bubbleMine =
  "max-w-[min(75vw,520px)] break-words [overflow-wrap:anywhere] rounded-lg rounded-tr-sm bg-wa-accentdeep px-2.5 py-1.5 text-[15px] leading-snug text-white shadow-sm";
const bubbleTheirs =
  "max-w-[min(75vw,520px)] break-words [overflow-wrap:anywhere] rounded-lg rounded-tl-sm border border-wa-border/70 bg-wa-panel2/90 px-2.5 py-1.5 text-[15px] leading-snug text-wa-text shadow-sm";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function textFromHtml(html) {
  return sanitizeHtml(html)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatBytes(bytes = 0) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / 104857.6) / 10} MB`;
}

function isSameIdentity(left, right) {
  if (left == null || right == null) return false;
  return String(left) === String(right);
}

const MessageStatus = ({ status }) => {
  const normalized = String(status || "send").toLowerCase();
  const isDelivered = normalized === "received" || normalized === "read";
  const isRead = normalized === "read";
  const label = isRead ? "Gelezen" : isDelivered ? "Ontvangen" : "Verzonden";

  return (
    <span
      title={label}
      className={classNames(
        "inline-flex items-center",
        isRead ? "text-wa-accent" : "text-wa-muted"
      )}
    >
      {isRead ? (
        <EyeIcon className="h-3.5 w-3.5" strokeWidth={2.2} />
      ) : (
        <span className="inline-flex items-center -space-x-1">
          <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.4} />
          {isDelivered ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.4} /> : null}
        </span>
      )}
    </span>
  );
};

const ListChats = ({
  chats,
  user,
  attachmentsByMessage = {},
  replyMap = {},
  pinnedMessageIds,
  searchMatchIds,
  onReply,
  onTogglePin,
  onEdit,
  onDelete,
  onDownloadAttachment,
}) => {
  const prepared = useMemo(() => {
    if (!chats?.length) return [];
    return [...chats]
      .sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
      .map((chat) => ({
        ...chat,
        safeBody: sanitizeHtml(chat?.body),
      }));
  }, [chats]);

  const timeline = useMemo(() => {
    if (!prepared.length) return [];
    const rows = [];
    let lastDayKey = null;
    prepared.forEach((chat, i) => {
      const dayKey = moment(chat?.datetime).format("YYYY-MM-DD");
      if (dayKey !== lastDayKey) {
        lastDayKey = dayKey;
        rows.push({ kind: "day", dayKey, anchor: chat });
      }
      const prev = i > 0 ? prepared[i - 1] : null;
      rows.push({ kind: "msg", chat, prev });
    });
    return rows;
  }, [prepared]);

  if (!timeline.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-20 text-center text-sm text-wa-muted">
        Nog geen berichten in dit gesprek.
      </div>
    );
  }

  const uid = user?.user_id;
  const hasPinned = (id) => Boolean(pinnedMessageIds?.has?.(String(id)));
  const hasSearchMatch = (id) => Boolean(searchMatchIds?.has?.(String(id)));

  return (
    <ul role="list" className="flex min-h-full flex-col gap-1 px-2 py-4 sm:px-4">
      {timeline.map((row) => {
        if (row.kind === "day") {
          const chat = row.anchor;
          return (
            <li key={`day-${row.dayKey}`} className="my-2 flex justify-center">
              <span className="rounded-lg border border-wa-border/70 bg-wa-panel/90 px-3 py-1 text-xs font-medium text-wa-muted shadow-sm">
                {moment(chat?.datetime).calendar(null, {
                  sameDay: "[Vandaag]",
                  lastDay: "[Gisteren]",
                  lastWeek: "LL",
                  sameElse: "LL",
                })}
              </span>
            </li>
          );
        }

        const { chat, prev } = row;
        const isMine = isSameIdentity(uid, chat?.author_id);
        const sameAuthorAsPrev =
          prev && isSameIdentity(prev.author_id, chat.author_id);
        const showAvatar = !isMine && !sameAuthorAsPrev;
        const attachments = attachmentsByMessage[String(chat.id)] || chat.attachments || [];
        const reply = replyMap[String(chat.id)];
        const isPinned = hasPinned(chat.id);
        const isSearchMatch = hasSearchMatch(chat.id);
        const isDeleted = Boolean(chat?.deleted_at || chat?.deleted_by);

        return (
          <li
            key={chat.id}
            id={`mail-message-${chat.id}`}
            className={classNames(
              "group flex w-full scroll-mt-32",
              isMine ? "justify-end" : "justify-start",
              sameAuthorAsPrev ? "mt-0.5" : "mt-2"
            )}
          >
            <div
              className={classNames(
                "flex max-w-[min(92%,680px)] items-end gap-2",
                isMine ? "flex-row-reverse" : "flex-row"
              )}
            >
              <div className="w-8 flex-shrink-0">
                {!isMine &&
                  (showAvatar ? (
                    chat?.avatar ? (
                      <img
                        src={chat.avatar}
                        alt=""
                        className="h-8 w-8 rounded-full border border-wa-border/60 object-cover"
                      />
                    ) : (
                      <div
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-wa-border/70 bg-gradient-to-br from-wa-panel2 to-wa-panel text-xs font-semibold text-wa-text"
                        aria-hidden
                      >
                        {avatarInitial(chat?.author)}
                      </div>
                    )
                  ) : null)}
              </div>
              <div
                className={classNames(
                  "flex min-w-0 flex-col",
                  isMine ? "items-end text-right" : "items-start text-left"
                )}
              >
                {!isMine && showAvatar ? (
                  <span className="mb-1 px-1 text-xs font-medium text-wa-accent">{chat?.author}</span>
                ) : null}
                <div
                  className={classNames(
                    isMine ? bubbleMine : bubbleTheirs,
                    isSearchMatch && "ring-2 ring-wa-accent/70",
                    isPinned && "ring-1 ring-wa-accent/45"
                  )}
                >
                  {reply ? (
                    <div
                      className={classNames(
                        "mb-1 rounded-md border-l-2 border-wa-accent px-2 py-1 text-left text-xs",
                        isMine ? "bg-black/10 text-white/80" : "bg-wa-panel text-wa-muted"
                      )}
                    >
                      <p className={classNames("truncate font-semibold", isMine ? "text-white" : "text-wa-text")}>
                        {reply.quoted_author || "Bericht"}
                      </p>
                      <p className="max-h-10 overflow-hidden text-left">
                        {textFromHtml(reply.quoted_body_preview) || "Bericht"}
                      </p>
                    </div>
                  ) : null}

                  {isDeleted ? (
                    <p className={classNames("italic", isMine ? "text-white/70" : "text-wa-muted")}>
                      Bericht verwijderd
                    </p>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: chat.safeBody }} />
                  )}

                  {attachments.length > 0 ? (
                    <div className="mt-2 flex flex-col gap-1.5">
                      {attachments.map((attachment) => (
                        <button
                          key={attachment.id || attachment.original_name}
                          type="button"
                          onClick={() => onDownloadAttachment?.(chat.id, attachment)}
                          className={classNames(
                            "flex min-w-0 items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition",
                            isMine
                              ? "border-white/15 bg-black/10 text-white hover:bg-black/20"
                              : "border-wa-border bg-wa-panel text-wa-text hover:bg-wa-hover"
                          )}
                        >
                          {String(attachment.mime_type || "").startsWith("image/") ? (
                            <PhotoIcon className="h-4 w-4 flex-shrink-0" />
                          ) : String(attachment.mime_type || "").includes("pdf") ? (
                            <DocumentTextIcon className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <PaperClipIcon className="h-4 w-4 flex-shrink-0" />
                          )}
                          <span className="min-w-0 flex-1 truncate">
                            {attachment.original_name || "Bijlage"}
                          </span>
                          <span className={classNames("shrink-0", isMine ? "text-white/70" : "text-wa-muted")}>
                            {formatBytes(attachment.size_bytes)}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div
                  className={classNames(
                    "mt-1 flex items-center gap-1 px-1 text-[11px] text-wa-muted",
                    isMine ? "justify-end" : "justify-start"
                  )}
                >
                  {isPinned ? <BookmarkIcon className="h-3.5 w-3.5 text-wa-accent" title="Vastgemaakt" /> : null}
                  <span>{moment(chat?.datetime).format("HH:mm")}</span>
                  {chat?.edited_at && !isDeleted ? <span>Bewerkt</span> : null}
                  {isMine ? <MessageStatus status={chat?.status} /> : null}
                </div>
                {!isDeleted ? (
                  <div
                    className={classNames(
                      "mt-1 flex items-center gap-1 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100",
                      isMine ? "justify-end" : "justify-start"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onReply?.(chat)}
                      className="rounded-full p-1.5 text-wa-muted hover:bg-wa-hover hover:text-wa-text"
                      title="Antwoorden"
                    >
                      <ArrowUturnLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onTogglePin?.(chat)}
                      className={classNames(
                        "rounded-full p-1.5 hover:bg-wa-hover",
                        isPinned ? "text-wa-accent" : "text-wa-muted hover:text-wa-text"
                      )}
                      title={isPinned ? "Losmaken" : "Vastmaken"}
                    >
                      <BookmarkIcon className="h-4 w-4" />
                    </button>
                    {isMine ? (
                      <button
                        type="button"
                        onClick={() => onEdit?.(chat)}
                        className="rounded-full p-1.5 text-wa-muted hover:bg-wa-hover hover:text-wa-text"
                        title="Bewerken"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                    ) : null}
                    {isMine ? (
                      <button
                        type="button"
                        onClick={() => onDelete?.(chat)}
                        className="rounded-full p-1.5 text-wa-muted hover:bg-wa-hover hover:text-red-400"
                        title="Verwijderen"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default React.memo(ListChats);
